import { Dropdown, Sheet, Toggle, Tooltip } from '@clickguard/design-system'
import {
	AutoRefundsFrequencyDTO,
	GoogleAdsAdAccountDTO,
	RefundRequestDTO
} from 'api-models'
import InfoCircle from '@untitled-ui/icons-react/build/esm/InfoCircle'
import moment from 'moment'
import { ReactNode, useState } from 'react'
import { toast } from 'react-toastify'
import { PlatformIcon } from 'components/platform-icon'
import { adAccountLabel } from 'utils/ad-account-label'
import { AdAccounts } from 'services/user/ad-accounts'
import { useAssetStore } from 'store'
import { patchAutoRefunds } from 'pages/workspace/asset/refunds/api'
import { AutoRefundsPatch } from 'pages/workspace/asset/refunds/types'
import { REFUNDS_AGENT_KB_URL, REFUNDS_SHEET_FLOAT_STYLE } from '../constants'

const FREQUENCY_OPTIONS = [
	{ value: 'weekly', label: 'Weekly (every 7 days)' },
	{ value: 'biweekly', label: 'Bi-weekly (every 14 days)' },
	{ value: 'monthly', label: 'Monthly (every 30 days)' }
]

const COOLDOWN_DAYS: Record<AutoRefundsFrequencyDTO, number> = {
	weekly: 7,
	biweekly: 14,
	monthly: 30
}

const InfoDot = ({ content }: { content: ReactNode }) => (
	<Tooltip theme="dark" position="top" portal content={content}>
		<InfoCircle className="w-3.5 h-3.5 text-t-gray-hold" />
	</Tooltip>
)

const CheckRow = ({
	label,
	value,
	color
}: {
	label: string
	value: string
	color: string
}) => (
	<div className="flex items-center gap-1.5">
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			className="shrink-0"
		>
			<circle cx="8" cy="8" r="6.25" stroke={color} strokeWidth="1.5" />
			<path
				d="M5.5 8.25L7 9.75L10.5 6.25"
				stroke={color}
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
		<span className="text-[14px] leading-[18px]">
			<span className="text-t-desc-gray">{label}: </span>
			<span className="font-medium text-t-coupon">{value}</span>
		</span>
	</div>
)

interface ConfigurationPanelProps {
	open: boolean
	onClose: () => void
	accountId: string
	adAccount: GoogleAdsAdAccountDTO
	items: RefundRequestDTO[]
}

/** §6 — Configuration Panel drawer (variants A/B/C). Spec B6. Figma 6404-49104 / 6472-203748 / 6468-193304. */
export const ConfigurationPanel = ({
	open,
	onClose,
	accountId,
	adAccount,
	items
}: ConfigurationPanelProps) => {
	const adAccounts = useAssetStore((s) => s.adAccounts)
	const setAdAccounts = useAssetStore((s) => s.setAdAccounts)
	const refreshAdAccounts = useAssetStore((s) => s.refreshAdAccounts)
	const [pending, setPending] = useState(false)
	const [resending, setResending] = useState(false)

	const accountTitle = adAccountLabel(adAccount)
	const accountNumber = adAccount.details?.account_number
	// An account with no name falls back to its number for the title, so the
	// number underneath would just repeat it. See adAccountLabel.
	const showAccountNumber =
		Boolean(accountNumber) && accountNumber !== accountTitle

	const mcc = adAccount.details?.mcc_support_connection
	const accessLost = mcc === 'ACCESS_LOST'
	// §6 — the row is a read-only Linked/Unlinked status (ACCESS_LOST gets its own
	// red treatment). A launched agent can sit in any non-LINKED state, so this must
	// reflect the real value rather than assume Linked.
	const linked = mcc === 'LINKED'
	const enabled = adAccount.preferences?.auto_refunds_enabled === true
	const frequency: AutoRefundsFrequencyDTO =
		adAccount.preferences?.auto_refunds_frequency ?? 'weekly'

	const nextAt =
		(adAccount.preferences?.auto_refunds_next_submission_at as
			| string
			| Date
			| null
			| undefined) ?? null

	const lastSubmitted = items
		.filter((i) => i.ad_account_id === adAccount.id && i.status === 'submitted')
		.sort((a, b) => {
			const ta = a.submission_date ? new Date(a.submission_date).getTime() : 0
			const tb = b.submission_date ? new Date(b.submission_date).getTime() : 0
			return tb - ta
		})[0]

	const fmt = (d: string | Date | null | undefined) =>
		d ? moment(d).format('MMM D, YYYY') : '–'
	const followingAt = nextAt
		? moment(nextAt).add(COOLDOWN_DAYS[frequency], 'days').toDate()
		: null

	const handlePatch = async (body: AutoRefundsPatch) => {
		setPending(true)
		const optimistic = adAccounts.map((a) =>
			a.id === adAccount.id
				? {
						...a,
						preferences: {
							...(a.preferences ?? {}),
							...(body.enabled !== undefined && {
								auto_refunds_enabled: body.enabled
							}),
							...(body.frequency !== undefined && {
								auto_refunds_frequency: body.frequency
							})
						}
				  }
				: a
		)
		setAdAccounts(optimistic)
		try {
			await patchAutoRefunds(accountId, adAccount.id, body)
			await refreshAdAccounts()
		} catch (e: any) {
			setAdAccounts(adAccounts)
			toast.error(
				e?.response?.data?.error?.message ??
					"Couldn't update the configuration"
			)
		} finally {
			setPending(false)
		}
	}

	const handleResend = async () => {
		setResending(true)
		try {
			await AdAccounts.sendMccLinkRequest(accountId, adAccount.id)
			await refreshAdAccounts()
			toast.success('Invitation resent')
		} catch {
			toast.error('Invitation request failed')
		} finally {
			setResending(false)
		}
	}

	return (
		<Sheet
			open={open}
			onClose={onClose}
			title="Configuration"
			style={REFUNDS_SHEET_FLOAT_STYLE}
		>
			<div className="flex flex-col gap-0">
				{/* Account section (top of the single grouped card) */}
				<div className="flex items-center justify-between gap-3 rounded-t-xl bg-white border border-t-checkbox border-b-0 p-4">
					<div className="flex items-center gap-3 min-w-0">
						<PlatformIcon platform="GADS" className="w-6 h-6 shrink-0" />
						<div className="min-w-0">
							<div className="text-[16px] leading-[22px] text-t-coupon truncate">
								{accountTitle}
							</div>
							{showAccountNumber && (
								<div className="text-[14px] leading-[18px] text-t-gray-hold">
									{accountNumber}
								</div>
							)}
						</div>
					</div>
					{/* "Active" badge — Figma 6454:192329: green outline pill, no dot,
					    #edfcf3 fill / #17b26a border+text, 14px medium, radius 16. Stays
					    green across variants per Figma (B6). */}
					<span className="inline-flex shrink-0 items-center rounded-[16px] border border-t-trend-up bg-t-badge-active px-[10px] py-[2px] text-[14px] leading-[18px] font-medium text-t-trend-up">
						Active
					</span>
				</div>

				{/* Settings section (joins the account section into one bordered card) */}
				<div className="rounded-b-xl bg-white border border-t-checkbox p-4 pb-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className="flex items-center gap-1.5 text-[16px] font-medium text-t-coupon">
							Automated refunds
							<InfoDot content="Turn automatic refund submissions on or off for this account." />
						</span>
						<Toggle
							size="big"
							checked={enabled}
							disabled={pending}
							onChange={(e) => handlePatch({ enabled: e.currentTarget.checked })}
						/>
					</div>

					<div className="flex items-center justify-between">
						<span className="flex items-center gap-1.5 text-[16px] font-medium text-t-coupon">
							MCC Access
							<InfoDot content="Whether ClickGuard's Manager account is linked to your Google Ads account." />
						</span>
						{accessLost ? (
							<span className="flex items-center gap-2">
								<span className="text-[13px] font-medium text-t-danger">
									Access lost
								</span>
								<button
									type="button"
									onClick={handleResend}
									disabled={resending}
									className="text-[13px] text-brand-tint-text underline disabled:opacity-50"
								>
									{resending ? 'Resending…' : 'Resend'}
								</button>
							</span>
						) : (
							<span className="text-[14px] leading-[18px] font-medium text-t-desc-gray">
								{linked ? 'Linked' : 'Unlinked'}
							</span>
						)}
					</div>

					<div className="flex items-center justify-between">
						<span className="text-[16px] font-medium text-t-coupon">
							Submission frequency
						</span>
						<Dropdown
							options={FREQUENCY_OPTIONS}
							value={frequency}
							disabled={!enabled || pending}
							onValueChange={(v) =>
								handlePatch({ frequency: v as AutoRefundsFrequencyDTO })
							}
						/>
					</div>

					{/* Schedule — flush full-width section at the card foot (B6). */}
					<div className="-mx-4 -mb-4 flex flex-col gap-[10px] rounded-b-xl border-t border-t-checkbox bg-table-band-subtle p-4">
						{accessLost ? (
							<CheckRow
								label="Next submission"
								value={fmt(nextAt)}
								color="var(--color-brand)"
							/>
						) : enabled ? (
							<>
								<CheckRow
									label="Last submission"
									value={fmt(lastSubmitted?.submission_date)}
									color="var(--t-trend-up)"
								/>
								<CheckRow
									label="Next submission"
									value={fmt(nextAt)}
									color="var(--color-brand)"
								/>
								<CheckRow
									label="Following submission"
									value={fmt(followingAt)}
									color="var(--color-brand)"
								/>
							</>
						) : (
							<CheckRow
								label="Last submission"
								value={fmt(lastSubmitted?.submission_date)}
								color="var(--t-trend-up)"
							/>
						)}
					</div>
				</div>

				<p className="text-[14px] leading-[18px] text-t-desc-gray mt-4">
					Visit the{' '}
					<a
						href={REFUNDS_AGENT_KB_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="font-medium text-brand-tint-text"
					>
						Knowledge base
					</a>{' '}
					for additional help.
				</p>
			</div>
		</Sheet>
	)
}
